import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { getMoodConfig } from "../stats/MoodChart";

const MoodDistribution = ({ chartData }) => {
    const { t } = useTranslation();

    // Safety check for when data is not provided
    if (!chartData || !Array.isArray(chartData) || chartData.length === 0) {
        return null;
    }

    const moodConfig = getMoodConfig();

    // Filter valid moods
    const validMoods = chartData.filter(entry => entry.mood !== null && entry.mood !== undefined);

    // Return null if no valid moods exist
    if (validMoods.length === 0) {
        return null;
    }

    // Count occurrences of each mood level
    const moodCounts = validMoods.reduce((counts, entry) => {
        counts[entry.mood] = (counts[entry.mood] || 0) + 1;
        return counts;
    }, {});

    const totalEntries = Object.values(moodCounts).reduce((sum, count) => sum + count, 0);

    // Check if there are any entries to display
    if (totalEntries === 0) {
        return null;
    }

    return (
        <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('moodChart.distribution')}
            </p>
            <div className="space-y-2">
                {Object.entries(moodConfig).map(([level, config]) => {
                    // Skip if config is undefined
                    if (!config) {
                        return null;
                    }

                    const count = moodCounts[level] || 0;
                    const percentage = totalEntries > 0 ? Math.round((count / totalEntries) * 100) : 0;

                    // Skip rendering mood levels with 0%
                    if (percentage === 0) {
                        return null;
                    }

                    return (
                        <div key={level} className="flex items-center text-xs">
                            <div className="w-8 flex justify-center">
                                <span>{config.emoji || '❓'}</span>
                            </div>
                            <div className="flex-1 mx-2">
                                <div className="relative h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${percentage}%` }}
                                        transition={{ duration: 0.5, ease: "easeOut" }}
                                        className="absolute top-0 left-0 h-full rounded-full"
                                        style={{ backgroundColor: config.color || '#6B7280' }}
                                    />
                                </div>
                            </div>
                            <div className="w-12 text-right font-medium text-gray-600 dark:text-gray-400">
                                {percentage}%
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default MoodDistribution;